// WordInput.jsx - Updated to support study mode
import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

const WordInput = ({
  word,
  hint,
  onComplete,
  onChange,
  disabled = false,
  tabIndex,
  error = false,
  helperText,
  isStudyMode = false,
}) => {
  // The target word we're trying to complete
  const targetWord = word || "";
  const targetLetters = targetWord.split("");

  // State for user input, completely separate from hint display
  const [userInput, setUserInput] = useState(Array(targetWord.length).fill(""));
  const [focused, setFocused] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const inputRef = useRef(null);

  // Process the hint if provided
  const hintPositions = getHintPositions(hint, targetWord.length);

  // When word changes, reset user input
  useEffect(() => {
    setUserInput(Array(targetWord.length).fill(""));
    setCursorPosition(0);
  }, [targetWord]);

  // When hint changes, preserve user input in non-hint positions
  useEffect(() => {
    if (hint) {
      const newHintPositions = getHintPositions(hint, targetWord.length);

      // Create a new user input array preserving values where possible
      const newUserInput = [...userInput];

      // Replace user input with hints where applicable
      for (let i = 0; i < newHintPositions.length; i++) {
        if (newHintPositions[i]) {
          newUserInput[i] = targetWord[i];
        }
      }

      setUserInput(newUserInput);
    }
  }, [hint]);

  // When input or cursor changes, update the input field
  useEffect(() => {
    if (inputRef.current && focused) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
        }
      }, 0);
    }
  }, [cursorPosition, focused]);

  // Check if the word is complete
  useEffect(() => {
    const fullInput = userInput.join("");

    // Check if all non-empty and complete
    const isComplete = userInput.every((char) => char !== "");

    if (isComplete && onComplete) {
      onComplete(fullInput.toLowerCase() === targetWord.toLowerCase());
    }

    // Notify parent component of changes
    if (onChange) {
      onChange(fullInput);
    }
  }, [userInput, targetWord, onComplete, onChange]);

  // Extract hint positions from hint string
  function getHintPositions(hintString, length) {
    if (!hintString) return Array(length).fill(false);

    const hintChars = hintString.split("");
    return hintChars.map((char) => char !== "_");
  }

  // Handle keydown events for navigation and editing
  const handleKeyDown = (e) => {
    if (disabled) return;

    switch (e.key) {
      case "ArrowLeft":
        e.preventDefault();
        // Move cursor left, even over hint positions
        if (cursorPosition > 0) {
          setCursorPosition(cursorPosition - 1);
        }
        break;

      case "ArrowRight":
        e.preventDefault();
        // Move cursor right, even over hint positions
        if (cursorPosition < targetWord.length - 1) {
          setCursorPosition(cursorPosition + 1);
        }
        break;

      case "Backspace":
        e.preventDefault();
        // If at a hint position, just move left
        if (hintPositions[cursorPosition]) {
          if (cursorPosition > 0) {
            setCursorPosition(cursorPosition - 1);
          }
        } else {
          // At an editable position, clear it and move left
          const newInput = [...userInput];
          newInput[cursorPosition] = "";
          setUserInput(newInput);

          if (cursorPosition > 0) {
            setCursorPosition(cursorPosition - 1);
          }
        }
        break;

      case "Delete":
        e.preventDefault();
        // If at a hint position, just move right
        if (hintPositions[cursorPosition]) {
          if (cursorPosition < targetWord.length - 1) {
            setCursorPosition(cursorPosition + 1);
          }
        } else {
          // At an editable position, clear it but stay in place
          const newInput = [...userInput];
          newInput[cursorPosition] = "";
          setUserInput(newInput);
        }
        break;

      case "Home":
        e.preventDefault();
        // Move to first position
        setCursorPosition(0);
        break;

      case "End":
        e.preventDefault();
        // Move to last position
        setCursorPosition(targetWord.length - 1);
        break;

      default:
        // Let other keys process normally
        break;
    }
  };

  // Handle direct character input
  const handleKeyPress = (e) => {
    if (disabled) return;

    // Only process printable characters
    if (e.key.length === 1) {
      e.preventDefault();

      // If at a hint position, just try to move right
      if (hintPositions[cursorPosition]) {
        if (cursorPosition < targetWord.length - 1) {
          setCursorPosition(cursorPosition + 1);
        }
      } else {
        // At an editable position, set the character and move right
        const newInput = [...userInput];
        newInput[cursorPosition] = e.key;
        setUserInput(newInput);

        if (cursorPosition < targetWord.length - 1) {
          setCursorPosition(cursorPosition + 1);
        }
      }
    }
  };

  // Handle input focus
  const handleFocus = () => {
    if (disabled) return;
    setFocused(true);
  };

  // Handle clicking on a character box
  const handleBoxClick = (index) => {
    if (disabled) return;

    // Allow clicking on any position, including hints
    inputRef.current.focus();
    setCursorPosition(index);
  };

  // Determine if a position is currently selected
  const isSelected = (index) => {
    return focused && cursorPosition === index && !isStudyMode;
  };

  // Get the display value for a position
  const getDisplayValue = (index) => {
    // In study mode, always show the target letter
    if (isStudyMode) {
      return targetLetters[index];
    }

    // If this is a hint position, show the target letter
    if (hintPositions[index]) {
      return targetLetters[index];
    }

    // Otherwise, show user input if any
    return userInput[index];
  };

  // Check if a position is empty and not a hint
  const isEmpty = (index) => {
    return !hintPositions[index] && userInput[index] === "";
  };

  // Check if a position has user input (not a hint)
  const hasUserInput = (index) => {
    return !hintPositions[index] && userInput[index] !== "";
  };

  return (
    <div style={{ width: "100%", maxWidth: "100%", margin: "0 auto" }}>
      <div style={{ position: "relative" }}>
        {/* Hidden input that captures keyboard events - only if not in study mode */}
        {!isStudyMode && (
          <input
            ref={inputRef}
            type="text"
            onFocus={handleFocus}
            onBlur={() => setFocused(false)}
            onKeyDown={handleKeyDown}
            onKeyPress={handleKeyPress}
            style={{
              opacity: 0,
              position: "absolute",
              top: 0,
              left: 0,
              height: 0,
              width: "100%",
            }}
            disabled={disabled}
            autoComplete="off"
            aria-label="Word input"
            tabIndex={tabIndex}
            spellCheck="false"
          />
        )}

        {/* Visual representation of the input */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            gap: "4px",
            marginTop: "16px",
            marginBottom: "8px",
          }}
        >
          {targetLetters.map((_, index) => {
            const isHint = isStudyMode || hintPositions[index];
            const isEmpty = !isHint && userInput[index] === "";
            const hasInput = !isHint && userInput[index] !== "";
            const selected = isSelected(index);

            return (
              <div
                key={index}
                onClick={!isStudyMode ? () => handleBoxClick(index) : undefined}
                style={{
                  width: "28px",
                  height: "36px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1rem",
                  borderBottom: `2px solid ${
                    error
                      ? "#f44336"
                      : selected
                      ? "#3f51b5"
                      : isStudyMode
                      ? "#4caf50" // Green for study mode
                      : isHint
                      ? "#9c9c9c"
                      : disabled
                      ? "#e0e0e0"
                      : "#757575"
                  }`,
                  cursor: isStudyMode || disabled ? "default" : "text",
                  transition: "all 0.2s",
                  backgroundColor: isStudyMode
                    ? "rgba(76, 175, 80, 0.05)" // Very light green background in study mode
                    : selected
                    ? "rgba(63, 81, 181, 0.08)"
                    : isHint && !isStudyMode
                    ? "#f5f5f5"
                    : "transparent",
                  position: "relative",
                }}
              >
                <span
                  style={{
                    transition: "all 0.2s",
                    color:
                      disabled && !isStudyMode
                        ? "#9e9e9e"
                        : isStudyMode
                        ? "#4caf50" // Green text in study mode
                        : isHint
                        ? "#212121"
                        : error
                        ? "#f44336"
                        : "#212121",
                    fontStyle: isHint && !isStudyMode ? "italic" : "normal",
                    fontWeight: hasInput || isStudyMode ? "500" : "normal",
                  }}
                >
                  {getDisplayValue(index)}
                </span>

                {/* Cursor indicator - only shown when position is selected and not in study mode */}
                {selected && !isStudyMode && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: "0px",
                      width: "100%",
                      height: "100%",
                      boxShadow: "0 0 0 1px rgba(63, 81, 181, 0.25)",
                      animation: "pulse 1.5s infinite",
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Helper text - same as Material-UI TextField */}
        {helperText && (
          <div
            style={{
              fontSize: "0.75rem",
              color: error ? "#f44336" : isStudyMode ? "#4caf50" : "#757575",
              marginTop: "3px",
              marginBottom: "8px",
            }}
          >
            {helperText}
          </div>
        )}

        {/* Add CSS animation for cursor pulse */}
        <style>{`
          @keyframes pulse {
            0% { opacity: 0.7; }
            50% { opacity: 0.3; }
            100% { opacity: 0.7; }
          }
        `}</style>
      </div>
    </div>
  );
};

WordInput.propTypes = {
  word: PropTypes.string,
  hint: PropTypes.string,
  onComplete: PropTypes.func,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  tabIndex: PropTypes.number,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  isStudyMode: PropTypes.bool,
};

export default WordInput;
